import { useMutation, useQuery } from "@tanstack/react-query";
import {
  getServiceBookingListingFn,
  serviceBookingEntryBookingStepFn,
  serviceBookingWorkSelectionFn,
  serviceBookingOptionSelectionFn,
  serviceBookingBookingFn,
  serviceBookingTyreChangeFn,
  serviceBookingBodyWorkFn,
  serviceBookingAdditionalCommentsFn,
  serviceBookingCRMFormFn,
  getServiceBookingInitializeFieldsFn,
  getServiceBookingShowSummaryFn,
  getServiceBookingCurrentStepFn,
  serviceBookingLocationFn,
  serviceBookingPickUpAddressFn,
  getServiceBookingAccidentReportDownloadFn,
} from "../../transactions/serviceBooking/newServiceBooking";
import { QUERY_KEYS } from "../../config/constants";
import { toast } from "../../components/ui/Toast/useToast";
import { size } from "lodash";

const { SERVICE_HISTORY, SERVICE_FLOW_ID, SERVICE_CREATE_INPUTS } = QUERY_KEYS;

const genericError = () => {
  toast({
    variant: "destructive",
    description: "Παρουσιάστηκε κάποιο πρόβλημα",
  });
};

// Fetching

export const useGetServiceListing = (plateNumber) => {
  return useQuery({
    queryKey: [SERVICE_HISTORY, plateNumber],
    queryFn: () => getServiceBookingListingFn(plateNumber),
    enabled: !!plateNumber,
    refetchOnWindowFocus: false,
  });
};

export const useGetInitiateService = (plateNumber) => {
  return useQuery(['initServiceBooking'], getServiceBookingInitializeFieldsFn, {
    queryKey: [SERVICE_CREATE_INPUTS, plateNumber],
    refetchOnWindowFocus: false
  });
};

export const useGetShowSummary = ({ serviceFlowId, enabled }) => {
  return useQuery({
    queryKey: ['SERVICE_FLOW_SUMMARY', serviceFlowId],
    queryFn: () => getServiceBookingShowSummaryFn(serviceFlowId),
    enabled: !!serviceFlowId && enabled,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
};

export const useGetCurrentStep = (serviceFlowId) => {
  return useQuery({
    queryKey: [SERVICE_FLOW_ID, serviceFlowId],
    queryFn: getServiceBookingCurrentStepFn(serviceFlowId),
    staleTime: 5 * 60 * 1000,
    cacheTime: Infinity,
    refetchOnWindowFocus: false
  });
};

export const useDownloadForm = (field = '') => {
  return useQuery({
    queryKey: ["BODYWORK_FORM", field],
    queryFn: () => getServiceBookingAccidentReportDownloadFn(field),
    staleTime: Infinity,
    cacheTime: Infinity,
    enabled: false,
    onSuccess: (data) => {
      // error handler
      //  toast('Success');
      if (data.exceptions) {
        if (size(data.exceptions) > 0) {
          return;
        }
      }
      const url = window.URL.createObjectURL(
        new Blob([data], {
          type: 'application/pdf',
        })
      );
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'AccidentReport.pdf');
      document.body.appendChild(link);
      link.click();
    },
  });
};

// Mutating

// ToDo:
// IF there are no different onSuccess handlers
// by the end of the project => Reduce boilerplate

export const useEntryBooking = () => {
  return useMutation({
    mutationFn: ({ payload }) =>
      serviceBookingEntryBookingStepFn({
        payload: payload,
      }),
    onError: genericError,
  });
};

export const useWorkSelection = () => {
  return useMutation({
    mutationFn: ({ serviceFlowId, payload }) =>
      serviceBookingWorkSelectionFn({
        serviceFlowId: serviceFlowId,
        payload: payload,
      }),
    onError: genericError,
  });
};

export const useLocation = () => {
  return useMutation({
    mutationFn: ({ serviceFlowId, payload }) =>
      serviceBookingLocationFn({
        serviceFlowId: serviceFlowId,
        payload: payload,
      }),
    onError: genericError,
  });
};

export const usePickUpAddress = () => {
  return useMutation({
    mutationFn: ({ serviceFlowId, payload }) =>
      serviceBookingPickUpAddressFn({
        serviceFlowId: serviceFlowId,
        payload: payload,
      }),
    onError: genericError,
  });
};

export const useOptionSelection = () => {
  return useMutation({
    mutationFn: ({ serviceFlowId, payload }) =>
      serviceBookingOptionSelectionFn({
        serviceFlowId: serviceFlowId,
        payload: payload,
      }),
    onError: genericError,
  });
};

export const useBooking = () => {
  return useMutation({
    mutationFn: ({ serviceFlowId, payload }) =>
      serviceBookingBookingFn({
        serviceFlowId: serviceFlowId,
        payload: payload,
      }),
    onError: genericError,
  });
};

export const useTyreChange = () => {
  return useMutation({
    mutationFn: ({ serviceFlowId, payload }) =>
      serviceBookingTyreChangeFn({
        serviceFlowId: serviceFlowId,
        payload: payload,
      }),
    onError: genericError,
  });
};

export const useBodyWork = () => {
  return useMutation({
    mutationFn: ({ serviceFlowId, payload }) =>
      serviceBookingBodyWorkFn({
        serviceFlowId: serviceFlowId,
        payload: payload,
      }),
    onError: genericError,
  });
};

export const useAdditionalComments = () => {
  return useMutation({
    mutationFn: ({ serviceFlowId, payload }) =>
      serviceBookingAdditionalCommentsFn({
        serviceFlowId: serviceFlowId,
        payload: payload,
      }),
    onError: genericError,
  });
};

export const useCRMForm = () => {
  return useMutation({
    mutationFn: ({ serviceFlowId, payload }) =>
      serviceBookingCRMFormFn({
        serviceFlowId: serviceFlowId,
        payload: payload,
      }),
    onError: genericError,
  });
};
